/**
 * PaymentButton Component
 *
 * Button to create and send payment links in WhatsApp conversations
 * Supports both Transbank and Mercado Pago providers
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CreditCard, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import type { PaymentProvider, PaymentDialogFormData } from '@/types/payment-types';
import {
  createPaymentLink,
  getPaymentProviderSettings,
  formatPaymentAmount,
  getPaymentProviderName,
} from '@/services/paymentService';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/error-utils';

interface PaymentButtonProps {
  conversationId: string;
  onPaymentCreated?: () => void;
  disabled?: boolean;
}

export function PaymentButton({ conversationId, onPaymentCreated, disabled }: PaymentButtonProps) {
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [availableProviders, setAvailableProviders] = useState<PaymentProvider[]>([]);
  const [formData, setFormData] = useState<PaymentDialogFormData>({
    provider: 'transbank',
    amount: 0,
    description: '',
    customer_email: '',
    customer_name: '',
    customer_phone: '',
  });

  const { t } = useTranslation('conversations');
  const { toast } = useToast();
  const { selectedTenant } = useTenantSelection();

  // Load available payment providers
  useEffect(() => {
    if (selectedTenant && open) {
      loadProviders();
    }
  }, [selectedTenant, open]);

  const loadProviders = async () => {
    if (!selectedTenant) return;

    const providers = await getPaymentProviderSettings(selectedTenant.id);
    const available: PaymentProvider[] = [];

    if (providers.transbank) available.push('transbank');
    if (providers.mercadopago) available.push('mercadopago');

    setAvailableProviders(available);

    // Set default provider to first available
    if (available.length > 0 && !available.includes(formData.provider)) {
      setFormData(prev => ({ ...prev, provider: available[0] }));
    }
  };

  const handleCreatePayment = async () => {
    if (!selectedTenant) {
      toast({
        title: t('payment.error'),
        description: t('payment.tenantNotAvailable'),
        variant: 'destructive',
      });
      return;
    }

    // Validation
    if (!formData.amount || formData.amount <= 0) {
      toast({
        title: t('payment.error'),
        description: t('payment.enterValidAmount'),
        variant: 'destructive',
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: t('payment.error'),
        description: t('payment.enterDescription'),
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);

    try {
      // Create payment link
      const response = await createPaymentLink(formData.provider, {
        tenant_id: selectedTenant.id,
        conversation_id: conversationId,
        amount: formData.amount,
        description: formData.description,
        customer_email: formData.customer_email || undefined,
        customer_name: formData.customer_name || undefined,
        customer_phone: formData.customer_phone || undefined,
      });

      if (!response.success || !response.payment_link) {
        throw new Error(response.error || t('payment.failedToCreate'));
      }

      // Send WhatsApp message with payment link
      const messageText = `💳 *Link de Pago - ${getPaymentProviderName(formData.provider)}*\n\n` +
        `Descripción: ${formData.description}\n` +
        `Monto: ${formatPaymentAmount(formData.amount)}\n\n` +
        `Haz clic en el siguiente enlace para realizar el pago:\n${response.payment_link}\n\n` +
        `${response.mock_mode ? '⚠️ *Modo de prueba* - No se realizará cargo real' : ''}`;

      const { error: sendError } = await supabase.functions.invoke('whatsapp-send-optimized', {
        body: {
          tenant_id: selectedTenant.id,
          conversation_id: conversationId,
          message: messageText,
          type: 'text',
        },
      });

      if (sendError) {
        console.error('Error sending WhatsApp message:', sendError);
        toast({
          title: t('payment.warning'),
          description: t('payment.createdButFailedToSend'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('payment.linkSent'),
          description: t('payment.sentSuccessfully', { provider: getPaymentProviderName(formData.provider) }) + (response.mock_mode ? t('payment.testMode') : ''),
        });
      }

      // Reset form and close dialog
      // G4: Use first available provider instead of hardcoded 'transbank' default
      setFormData({
        provider: availableProviders[0] || formData.provider,
        amount: 0,
        description: '',
        customer_email: '',
        customer_name: '',
        customer_phone: '',
      });
      setOpen(false);

      // Notify parent component
      if (onPaymentCreated) {
        onPaymentCreated();
      }
    } catch (error: unknown) {
      console.error('Error creating payment:', error);
      toast({
        title: t('payment.error'),
        description: getErrorMessage(error) || t('payment.failedToCreate'),
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isCreating) {
      setOpen(false);
    } else if (newOpen) {
      setOpen(true);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <CreditCard className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('payment.createLink')}</DialogTitle>
            <DialogDescription>
              {t('payment.createLinkDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Provider Selection */}
            <div className="grid gap-2">
              <Label htmlFor="provider">{t('payment.provider')}</Label>
              {availableProviders.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('payment.noProvidersConfigured')}
                </p>
              ) : (
                <Select
                  value={formData.provider}
                  onValueChange={(value) =>
                    setFormData(prev => ({ ...prev, provider: value as PaymentProvider }))
                  }
                >
                  <SelectTrigger id="provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProviders.map(provider => (
                      <SelectItem key={provider} value={provider}>
                        {getPaymentProviderName(provider)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Amount */}
            <div className="grid gap-2">
              <Label htmlFor="amount">{t('payment.amount')}</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="1"
                placeholder={t('payment.amountPlaceholder')}
                value={formData.amount || ''}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))
                }
              />
              {formData.amount > 0 && (
                <p className="text-sm text-muted-foreground">
                  {formatPaymentAmount(formData.amount)}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">{t('payment.description')}</Label>
              <Textarea
                id="description"
                placeholder={t('payment.descriptionPlaceholder')}
                value={formData.description}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, description: e.target.value }))
                }
                rows={2}
              />
            </div>

            {/* Optional: Customer Details */}
            <div className="grid gap-2">
              <Label htmlFor="customer_name">{t('payment.customerName')}</Label>
              <Input
                id="customer_name"
                type="text"
                placeholder={t('payment.customerNamePlaceholder')}
                value={formData.customer_name}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, customer_name: e.target.value }))
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer_email">{t('payment.customerEmail')}</Label>
              <Input
                id="customer_email"
                type="email"
                placeholder={t('payment.customerEmailPlaceholder')}
                value={formData.customer_email}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, customer_email: e.target.value }))
                }
              />
            </div>

            {/* G4: Show phone field for any provider that may use it (not just mercadopago) */}
            {formData.provider !== 'transbank' && (
              <div className="grid gap-2">
                <Label htmlFor="customer_phone">{t('payment.customerPhone')}</Label>
                <Input
                  id="customer_phone"
                  type="tel"
                  placeholder={t('payment.customerPhonePlaceholder')}
                  value={formData.customer_phone}
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, customer_phone: e.target.value }))
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isCreating}
            >
              {t('payment.cancel')}
            </Button>
            <Button
              onClick={handleCreatePayment}
              disabled={isCreating || availableProviders.length === 0}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('payment.creating')}
                </>
              ) : (
                t('payment.createAndSend')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
